import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Loader2, RefreshCw, ArrowUp, ArrowRight, ArrowDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRAITS = [
  {
    key: 'openness',
    label: 'Curiosity',
    color: '#7B4FD8',
    description: 'How open you are to new experiences and ideas. Higher curiosity helps you connect with a wider range of people.',
  },
  {
    key: 'conscientiousness',
    label: 'Follow-through',
    color: '#2563EB',
    description: 'How consistently you complete what you start. This is one of the strongest predictors of long-term social growth.',
  },
  {
    key: 'extraversion',
    label: 'Social Energy',
    color: '#F5831F',
    description: 'How energised you feel in social situations. This grows with practice \u2014 that\u2019s exactly what HiBFF is for.',
  },
  {
    key: 'agreeableness',
    label: 'Empathy',
    color: '#2EAA5C',
    description: 'How naturally you tune in to how others are feeling. This is the foundation of every meaningful friendship.',
  },
  {
    key: 'neuroticism',
    label: 'Emotional Sensitivity',
    color: '#EC4899',
    description: 'How strongly you feel and respond to emotions. High sensitivity is a strength \u2014 it means you care deeply.',
  },
];

const GrowthArrow = ({ direction }) => {
  if (direction === 'up') return <ArrowUp size={14} style={{ color: '#2EAA5C' }} />;
  if (direction === 'down') return <ArrowDown size={14} style={{ color: '#F59E0B' }} />;
  return <ArrowRight size={14} style={{ color: '#9CA3AF' }} />;
};

const TraitBar = ({ trait, score, growth, showGrowth }) => {
  const pct = Math.max(0, Math.min(100, (score / 5) * 100));
  return (
    <div className="mb-6" data-testid={`trait-${trait.key}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold" style={{ color: '#1A1209' }}>{trait.label}</span>
          {showGrowth && growth && <GrowthArrow direction={growth} />}
        </div>
      </div>
      <div className="w-full h-3 rounded-full" style={{ backgroundColor: '#F0F0F0' }}>
        <div
          className="h-full rounded-full transition-all duration-1000 ease-out"
          style={{ width: `${pct}%`, backgroundColor: trait.color }}
        />
      </div>
      <p className="text-xs mt-1.5" style={{ color: '#888' }}>{trait.description}</p>
    </div>
  );
};

const OceanProfile = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const getHeaders = () => {
    const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const loadProfile = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ocean/profile`, { headers: getHeaders() });
      const data = await res.json();
      setProfile(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#7B4FD8' }} />
        </div>
      </DashboardLayout>
    );
  }

  if (!profile?.has_profile) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto py-12 text-center" data-testid="ocean-no-profile">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#7B4FD815' }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#7B4FD8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
            </svg>
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: '#1A1209' }}>Your profile isn&apos;t ready yet</h1>
          <p className="text-sm" style={{ color: '#666' }}>
            Complete three conversations with your AvA and you&apos;ll be invited to build your personal profile.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  const scores = profile.scores || {};
  const growth = profile.growth || {};
  const showGrowth = profile.history_count > 0;

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto" data-testid="ocean-profile-page">
        <h1 className="text-2xl font-bold mb-1" style={{ color: '#1A1209' }}>My Profile</h1>
        <p className="text-sm mb-8" style={{ color: '#888' }}>Your personal social development profile, based on your own responses.</p>

        {/* Trait Bars */}
        <div className="bg-white rounded-xl p-6 mb-6" style={{ border: '1px solid #E5E7EB' }} data-testid="ocean-trait-bars">
          {TRAITS.map((trait) => (
            <TraitBar
              key={trait.key}
              trait={trait}
              score={scores[trait.key] || 3}
              growth={growth[trait.key]}
              showGrowth={showGrowth}
            />
          ))}
        </div>

        {/* Reflection */}
        {profile.reflection && (
          <div className="bg-white rounded-xl p-6 mb-6" style={{ border: '1px solid #E5E7EB' }} data-testid="ocean-reflection">
            <p className="text-sm leading-relaxed" style={{ color: '#444' }}>
              {profile.reflection}
            </p>
          </div>
        )}

        {/* Retake */}
        <div className="flex items-center justify-between p-5 bg-white rounded-xl" style={{ border: '1px solid #E5E7EB' }}>
          <div>
            <p className="text-xs font-medium" style={{ color: '#888' }}>
              Last completed: {new Date(profile.completed_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
          {profile.can_retake ? (
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-sm font-medium"
              style={{ backgroundColor: '#7B4FD8' }}
              data-testid="ocean-retake-btn"
            >
              <RefreshCw size={14} /> Refresh my profile
            </button>
          ) : (
            <div className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm" style={{ backgroundColor: '#F5F5F5', color: '#AAA' }}>
              <RefreshCw size={14} /> Available in {profile.days_until_retake} days
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default OceanProfile;
